<template>
  <div id="SetUpDialog_main">
    <el-dialog
      :title="$t('SmartLocker.Setting')"
      :visible.sync="dialogVisible"
      width="60%"
      @close="closeDialog"
    >
      <el-tabs v-model="activeName" class="setUpTabs">
        <el-tab-pane :label="$t('SmartLocker.OwnerGroupManagement')" name="group">
          <el-table
            class="list"
            :data="localGroupList.filter(g => g.Id !=='')"
            :empty-text="$t('SmartLocker.NoOwnerGroupData')"
            tooltip-effect="dark"
          >
            <el-table-column type="index" width="60" />

            <el-table-column :label="$t('SmartLocker.SmartLockerPosition')" width="220">
              <template slot-scope="{ row, $index }">
                <div v-if="!row.edited">
                  {{ row.name }}
                </div>
                <el-input
                  v-else
                  :ref="'nameInput' + $index"
                  v-model="row.name"
                  maxlength="10"
                  :placeholder="$t('SmartLocker.NameInputPlaceholder')"
                />
              </template>
            </el-table-column>

            <el-table-column :label="$t('SmartLocker.SmartLockerAvailableBuilding')" min-width="200">
              <template slot-scope="{ row }">
                <div v-if="row.Tags && row.Tags.length" class="tag-list">
                  <p v-for="(tag, i) in row.Tags" :key="i" size="small">
                    {{ tag }}
                  </p>
                </div>
                <span v-else>—</span>
              </template>
            </el-table-column>

            <el-table-column :label="$t('General.Operation')" width="220">
              <template slot-scope="{ row, $index }">
                <template v-if="row.edited">
                  <el-button
                    size="mini"
                    type="primary"
                    plain
                    :loading="row._pending === 'save'"
                    @click="submitRow(row)"
                  >
                    {{ $t('General.OK') }}
                  </el-button>
                  <el-button size="mini" plain @click="cancelRow(row)">
                    {{ $t('General.Cancel') }}
                  </el-button>
                </template>
                <template v-else>
                  <div>
                    <el-tooltip :disabled="loginUserInfo.IsAdmin" :content="$t('Auth.NotHighestAdmin')" placement="top">
                      <el-button
                        size="mini"
                        plain
                        :disabled="!loginUserInfo.IsAdmin || isContractExpired || !hasCurrentContract"
                        @click="editRow(row, $index, $event)"
                      >
                        {{ $t('SmartLocker.Rename') }}
                      </el-button>
                    </el-tooltip>
                  </div>
                </template>
              </template>
            </el-table-column>
          </el-table>

          <TableLoading :is-loading="loading" />
        </el-tab-pane>
      </el-tabs>
    </el-dialog>
  </div>
</template>

<script>
import TableLoading from '@/components/TableLoading'
import { mapiSmartLocker } from '@/api/SmartLocker.js'
import { mapState, mapActions } from 'vuex'
import { LockerContractStatus } from '@/views/SmartLocker/models'

export default {
  name: 'SetUpDialog',
  components: { TableLoading },
  data () {
    return {
      dialogVisible: false,
      activeName: 'group',
      loading: false,
      loadingInstance: null,
      loginUserInfo: {},
      localGroupList: []// ✅ 本地可編輯的版本
    }
  },
  computed: {
    ...mapState('smartLocker', ['ownerGroupList', 'lockerContract', 'hasCurrentContract']),
    isContractExpired () {
      return this.lockerContract.status === LockerContractStatus.Expired
    }
  },
  watch: {
    // 當 store 更新時，同步刷新本地群組列表
    ownerGroupList: {
      immediate: true,
      handler (newVal) {
        const editedMap = Object.fromEntries(
          this.localGroupList.filter(g => g.edited).map(g => [g.Id, true])
        )

        this.localGroupList = newVal.map(g => ({
          ...g,
          name: g.Name,
          tags: g.Tags,
          edited: !!editedMap[g.Id], // ✅ 保留正在編輯的列
          oldName: g.Name,
          _pending: null,
          _busy: false
        }))
      }
    }
  },
  methods: {
    ...mapActions('smartLocker', ['fetchGroupList']),
    openDialog () {
      this.dialogVisible = true
      this.init()
    },
    closeDialog () {
      this.dialogVisible = false
      // 關閉時，重設所有列的編輯狀態
      this.localGroupList.forEach(g => {
        g.edited = false
        g._pending = null
      })
    },
    async init () {
      this.loading = true
      this.fullScreenLoading(true)
      try {
        await this.fetchGroupList()
      } catch (err) {
        this.$message.error(this.$t('SmartLocker.OwnerGroupListFetchFail'))
      } finally {
        this.loading = false
        this.fullScreenLoading(false)
      }
      this.initLoginUserInfo()
    },
    editRow (row, index, evt) {
      row.edited = true
      row.oldName = row.name
      if (evt && evt.currentTarget) evt.currentTarget.blur()
    },
    async submitRow (row) {
      const name = (row.name || '').trim()
      if (!name) return this.alert(this.$t('SmartLocker.EmptyInputError'), 'error')
      if (name === row.oldName) {
        row.edited = false
        return
      }

      // ✅ 檢查是否重複名稱（排除自己這一列）
      const duplicate = this.localGroupList.some(g =>
        g.Id !== row.Id && g.name.trim() === name
      )
      if (duplicate) {
        this.alert(this.$t('SmartLocker.DuplicateOwnerGroupNameError'), 'error')
        return
      }

      try {
        row._pending = 'save'
        this.fullScreenLoading(true)
        const res = await mapiSmartLocker.UpdateSmartLockerOwnerGroupName({
          OwnerGroupId: row.Id,
          Name: row.name
        })
        if (res?.data?.Status === 1) {
          this.$message.success(this.$t('SmartLocker.RenameSuccess'))
          row.edited = false
          row.oldName = name
          await this.fetchGroupList() // ✅ 重新拉取櫃體位置最新資料
        }
      } catch (err) {
        console.error(err)
      } finally {
        row._pending = null
        this.fullScreenLoading(false)
      }
    },
    cancelRow (row) {
      row.edited = false
      row.name = row.oldName
    },
    alert (message, type = 'error') {
      const title = type === 'error' ? this.$t('General.Error') : this.$t('General.Success')
      this.$alert(message, title, { confirmButtonText: this.$t('General.OK'), type })
    },
    fullScreenLoading (display) {
      if (display) {
        if (!this.loadingInstance) {
          this.loadingInstance = this.$loading({
            lock: true,
            text: this.$t('General.Processing'),
            spinner: 'el-icon-loading',
            background: 'rgba(0, 0, 0, 0.7)',
            customClass: 'SetUpDialog_loading'
          })
        }
      } else if (this.loadingInstance) {
        this.loadingInstance.close()
        this.loadingInstance = null
      }
    },
    initLoginUserInfo () {
      this.loginUserInfo = JSON.parse(localStorage.getItem('loginUserInfo'))
    }
  }
}
</script>

<style lang="scss" scoped>

#SetUpDialog_main {
  ::v-deep .setUpTabs {
    .el-tabs__header {
      margin: 0;
    }
    .el-tabs__active-bar {
      height: 4px;
    }
    .el-tabs__nav-wrap::after {
      height: 4px;
    }
    .el-tabs__item {
      height: 46px;
      min-width: 120px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .el-tab-pane {
      padding-top: 25px;
    }
  }

  ::v-deep .el-table.list {
    .el-table__row td {
      height: 58px;
      vertical-align: middle;
    }
  }

  .tag-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  ::v-deep .el-table {
    @include basicTable(false);
  }
  ::v-deep .el-dialog{
    @include basicDialog();
    .el-dialog__body {
      padding: 20px 15px 20px 15px;
    }
  }
}
</style>

<style lang="scss">
.SetUpDialog_loading {
  z-index: 10000 !important;
}
</style>
